import type { Spec, UIElement } from '@json-render/core';
import is from '@sindresorhus/is';

import {
    generativeUiComponents,
    isInAppPath,
    MAX_CHOICE_OPTIONS,
    MAX_ENTITY_CARDS,
    MAX_LINE_POINTS,
    MAX_LIST_ITEMS,
    MAX_TABLE_COLUMNS,
    MAX_TABLE_ROWS,
    MAX_TABS,
    MAX_TIMELINE_ITEMS,
    NAVIGATE_ACTION,
} from './catalog';
import { type RenderUiElement, renderUiArgumentsSchema } from './render-ui-tool';

export const MAX_UI_ELEMENTS = 100;
export const MAX_UI_SPEC_BYTES = 64 * 1024;

type SpecConversion =
    | { readonly isValid: true; readonly spec: Spec }
    | { readonly isValid: false; readonly reason: string };

const invalid = (reason: string): SpecConversion => ({ isValid: false, reason });

const toUiElement = (element: RenderUiElement): UIElement => {
    const base = { children: element.children, props: element.props, type: element.type };
    return element.type === 'Button' ? { ...base, on: { press: element.on.press } } : base;
};

// The renderer walks children recursively, so a cycle would never end; every element is reached once.
const findTreeProblem = (root: string, elements: ReadonlyMap<string, RenderUiElement>): string | null => {
    const visited = new Set<string>();
    const pending = [root];
    while (pending.length > 0) {
        const id = pending.pop();
        if (is.undefined(id)) break;
        if (visited.has(id)) return `element "${id}" is reached twice`;
        visited.add(id);
        const element = elements.get(id);
        if (is.undefined(element)) return `element "${id}" does not exist`;
        if (!generativeUiComponents[element.type].hasChildren && element.children.length > 0) {
            return `${element.type} cannot have children`;
        }
        pending.push(...element.children);
    }
    return null;
};

type ElementOf<TType extends RenderUiElement['type']> = Extract<RenderUiElement, { type: TType }>;

const isOutOfRange = (count: number, min: number, max: number): boolean => count < min || count > max;

const findTableProblem = ({ props: { columns, rows } }: ElementOf<'Table'>): string | null => {
    if (isOutOfRange(columns.length, 1, MAX_TABLE_COLUMNS)) return `a table needs 1 to ${MAX_TABLE_COLUMNS} columns`;
    if (rows.length > MAX_TABLE_ROWS) return `a table holds at most ${MAX_TABLE_ROWS} rows`;
    return rows.every((row) => row.length === columns.length) ? null : 'a table row does not match its columns';
};

const findTabsProblem = ({ children, props: { labels } }: ElementOf<'Tabs'>): string | null => {
    if (isOutOfRange(labels.length, 2, MAX_TABS)) return `tabs need 2 to ${MAX_TABS} labels`;
    return labels.length === children.length ? null : 'tabs need one label per child';
};

const findChoiceProblem = ({ props: { options, recommended } }: ElementOf<'Choice'>): string | null => {
    if (isOutOfRange(options.length, 2, MAX_CHOICE_OPTIONS)) return `a choice needs 2 to ${MAX_CHOICE_OPTIONS} options`;
    const labels = options.map(({ label }) => label);
    if (new Set(labels).size !== labels.length) return 'a choice repeats an option';
    return recommended === null || labels.includes(recommended)
        ? null
        : 'a choice recommends an option it does not offer';
};

const findLineChartProblem = ({ props: { points } }: ElementOf<'LineChart'>): string | null => {
    if (isOutOfRange(points.length, 2, MAX_LINE_POINTS)) return `a line chart needs 2 to ${MAX_LINE_POINTS} points`;
    return points.every(({ value }) => Number.isFinite(value)) ? null : 'a line chart value is not a number';
};

const findBarChartProblem = ({ props: { items } }: ElementOf<'BarChart'>): string | null => {
    if (isOutOfRange(items.length, 1, MAX_LIST_ITEMS)) return `a bar chart needs 1 to ${MAX_LIST_ITEMS} bars`;
    return items.every(({ value }) => Number.isFinite(value) && value >= 0)
        ? null
        : 'a bar chart value is negative or not a number';
};

const findElementProblem = (element: RenderUiElement): string | null => {
    switch (element.type) {
        case 'BarChart':
            return findBarChartProblem(element);
        case 'Button':
            return element.on.press.action === NAVIGATE_ACTION && !isInAppPath(element.on.press.params.path)
                ? 'a button navigates outside the app'
                : null;
        case 'Choice':
            return findChoiceProblem(element);
        case 'LineChart':
            return findLineChartProblem(element);
        case 'KeyValue':
            return isOutOfRange(element.props.items.length, 1, MAX_LIST_ITEMS)
                ? `a key value list needs 1 to ${MAX_LIST_ITEMS} items`
                : null;
        case 'Progress':
            return element.props.max > 0 ? null : 'a progress bar needs a positive max';
        case 'Table':
            return findTableProblem(element);
        case 'Tabs':
            return findTabsProblem(element);
        case 'Timeline':
            return element.props.items.length > MAX_TIMELINE_ITEMS
                ? `a timeline holds at most ${MAX_TIMELINE_ITEMS} items`
                : null;
        default:
            return null;
    }
};

// The same budget the producer enforces on the tool arguments, checked before any parsing.
const findBudgetProblem = (input: unknown): string | null => {
    if (is.plainObject(input) && is.array(input['elements']) && input['elements'].length > MAX_UI_ELEMENTS) {
        return `more than ${MAX_UI_ELEMENTS} elements`;
    }
    const serialized = JSON.stringify(input) ?? '';
    const isOverBudget =
        serialized.length > MAX_UI_SPEC_BYTES ||
        (serialized.length * 3 > MAX_UI_SPEC_BYTES && new TextEncoder().encode(serialized).length > MAX_UI_SPEC_BYTES);
    return isOverBudget ? `more than ${MAX_UI_SPEC_BYTES} bytes` : null;
};

const convert = (input: unknown): SpecConversion => {
    const budgetProblem = findBudgetProblem(input);
    if (!is.null(budgetProblem)) return invalid(budgetProblem);
    const parsed = renderUiArgumentsSchema.safeParse(input);
    if (!parsed.success) return invalid('the arguments do not match the render_ui schema');
    const { elements: list, root } = parsed.data;
    // Every live card loads its own data, so a UI with many of them would fire a burst of requests at once.
    if (list.filter(({ type }) => type === 'EntityCard').length > MAX_ENTITY_CARDS) {
        return invalid(`more than ${MAX_ENTITY_CARDS} live cards`);
    }

    const elements = new Map<string, RenderUiElement>();
    for (const element of list) {
        if (elements.has(element.id)) return invalid(`element id "${element.id}" is used twice`);
        const elementProblem = findElementProblem(element);
        if (!is.null(elementProblem)) return invalid(elementProblem);
        elements.set(element.id, element);
    }
    const treeProblem = findTreeProblem(root, elements);
    if (!is.null(treeProblem)) return invalid(treeProblem);

    return {
        isValid: true,
        spec: {
            elements: Object.fromEntries([...elements].map(([id, element]) => [id, toUiElement(element)])),
            root,
        },
    };
};

// An activity's content keeps its identity across renders, so each spec is read once.
const conversions = new WeakMap<object, SpecConversion>();

/** Reads the `render_ui` arguments of a ui block into a json-render spec, or says why it cannot. */
export const toJsonRenderSpec = (input: unknown): SpecConversion => {
    if (!is.object(input)) return convert(input);
    const cached = conversions.get(input);
    if (!is.undefined(cached)) return cached;
    const conversion = convert(input);
    conversions.set(input, conversion);
    return conversion;
};
